import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  FormControl,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import {
  fetchUtilizationReport,
  fetchUtilizationUcReport,
  generateUtilizationCertificate,
  sendUtilizationCertificateOtp,
} from "../../../helper/Api";
import { DEFAULT_EXPENSE_DATE_BASIS } from "../../../RTK/Slices/AppConfigSlice";
import UtilizationReportCards from "./UtilizationReportCards";
import UtilizationUcReportModal from "./UtilizationUcReportModal";
import UtilizationUcOtpModal from "./UtilizationUcOtpModal";
import { normalizeUtilizationReportResponse } from "./utilizationReportAdapter";
import {
  buildUcReportRequestPayload,
  buildUcVerifyPayload,
  buildChecksExercisedState,
  getCertificateKey,
  normalizeUtilizationUcReportResponse,
} from "./utilizationUcReportAdapter";
import { openUtilizationReportPrintWindow } from "./utilizationReportPrintHtml";
import {
  VIEW_TYPES,
  getFyMonthOptions,
} from "./utilizationReportUtils";
import "./UtilizationReport.css";

const UtilizationReport = ({ fy_list }) => {
  const { authData } = useSelector((state) => state.authData);
  const expenseDateBasis = useSelector(
    (state) => state.appConfig?.expenseDateBasis || DEFAULT_EXPENSE_DATE_BASIS
  );
  const fyId = localStorage.getItem("globalDateId");

  const [viewType, setViewType] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [rawReportData, setRawReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ucModalOpen, setUcModalOpen] = useState(false);
  const [ucType, setUcType] = useState("");
  const [ucLoading, setUcLoading] = useState(false);
  const [ucSaving, setUcSaving] = useState(false);
  const [ucReportData, setUcReportData] = useState(null);
  const [checksExercisedByScheme, setChecksExercisedByScheme] = useState({});
  const [ucOtpModalOpen, setUcOtpModalOpen] = useState(false);
  const [ucOtp, setUcOtp] = useState("");
  const [ucOtpLoading, setUcOtpLoading] = useState(false);
  const [isSoeCollapsed, setIsSoeCollapsed] = useState(false);

  const isL3User =
    String(authData?.user?.user_type || "").toLowerCase() === "l3";

  const authScopeKey = [
    authData?.user?.id,
    authData?.user?.department_id,
    authData?.user?.user_type,
  ].join("|");

  useEffect(() => {
    setViewType("");
    setSelectedMonth("");
    setRawReportData(null);
    setLoading(false);
    setUcModalOpen(false);
    setUcType("");
    setUcReportData(null);
    setChecksExercisedByScheme({});
    setUcLoading(false);
    setUcSaving(false);
    setUcOtpModalOpen(false);
    setUcOtp("");
    setUcOtpLoading(false);
  }, [authScopeKey]);

  const selectedFy = useMemo(
    () => fy_list?.find((fy) => String(fy.id) === String(fyId)),
    [fy_list, fyId]
  );

  const monthOptions = useMemo(
    () => getFyMonthOptions(selectedFy),
    [selectedFy]
  );

  const getFilterContext = () => ({
    selectedFy,
    selectedMonth,
    monthOptions,
    viewType,
  });

  const buildApiPayload = () => ({
    financial_year_id: fyId,
    year: selectedFy?.year || "",
    month: selectedMonth,
    department_id: authData?.user?.department_id || "",
    expense_date_basis: expenseDateBasis,
  });

  const reportData = useMemo(() => {
    if (!rawReportData || !viewType) return null;
    return normalizeUtilizationReportResponse(rawReportData, getFilterContext());
  }, [rawReportData, viewType, selectedFy, selectedMonth, monthOptions]);

  const validateFilters = () => {
    if (!fyId) {
      toast.error("Please select a financial year", {
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    if (!viewType) {
      toast.error("Please select view card type", {
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    if (!selectedMonth) {
      toast.error("Please select a month", {
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    return true;
  };

  const handleLoadCard = async () => {
    if (!validateFilters()) return;

    try {
      setLoading(true);
      const { data } = await fetchUtilizationReport(buildApiPayload());

      if (!data?.status) {
        toast.error(data?.message || "Something went wrong", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      setRawReportData(data.result ?? data.data ?? data);
    } catch (error) {
      toast.error("Something went wrong", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setViewType("");
    setSelectedMonth("");
    setRawReportData(null);
    setUcModalOpen(false);
    setUcType("");
    setUcReportData(null);
    setChecksExercisedByScheme({});
    setUcLoading(false);
    setUcSaving(false);
    setUcOtpModalOpen(false);
    setUcOtp("");
    setUcOtpLoading(false);
  };

  const selectedMonthLabel = useMemo(() => {
    const option = monthOptions.find((item) => item.value === selectedMonth);
    return option?.label || "-";
  }, [monthOptions, selectedMonth]);

  const resetUcDraftState = () => {
    setUcReportData(null);
    setChecksExercisedByScheme({});
    setUcOtpModalOpen(false);
    setUcOtp("");
    setUcOtpLoading(false);
  };

  const resetUcFlowState = () => {
    setUcModalOpen(false);
    setUcType("");
    setUcLoading(false);
    setUcSaving(false);
    resetUcDraftState();
  };

  const buildUcReportPayload = (ucTypeValue = ucType) =>
    buildUcReportRequestPayload({
      fyId,
      month: selectedMonth,
      ucType: ucTypeValue,
      departmentId: authData?.user?.department_id || "",
    });

  const generateUcCertificate = async (payload) => {
    const { data } = await fetchUtilizationUcReport(payload);
    return data;
  };

  const finalizeUcCertificate = async (payload) => {
    const { data } = await generateUtilizationCertificate(payload);
    return data;
  };

  const hasReportCards = Boolean(reportData?.cards?.length);

  const handleOpenUcModal = async () => {
    if (!hasReportCards) {
      toast.error("Please load card data first", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    if (!validateFilters()) return;

    setUcType("");
    resetUcDraftState();
    setUcModalOpen(true);
  };

  const handleCloseUcModal = () => {
    resetUcFlowState();
  };

  const handleCheckChange = (certificateKey, checkIndex, value) => {
    setChecksExercisedByScheme((prev) => ({
      ...prev,
      [certificateKey]: (prev[certificateKey] || [""]).map((item, idx) =>
        idx === checkIndex ? value : item
      ),
    }));
  };

  const handleCheckAdd = (certificateKey) => {
    setChecksExercisedByScheme((prev) => ({
      ...prev,
      [certificateKey]: [...(prev[certificateKey] || [""]), ""],
    }));
  };

  const handleCheckRemove = (certificateKey, checkIndex) => {
    setChecksExercisedByScheme((prev) => {
      const current = prev[certificateKey] || [""];
      if (current.length <= 1) {
        return prev;
      }

      return {
        ...prev,
        [certificateKey]: current.filter((_, idx) => idx !== checkIndex),
      };
    });
  };

  const handleGenerateUcReport = async () => {
    if (!ucType) {
      toast.error("Please select UC type", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    if (!validateFilters()) return;

    try {
      setUcLoading(true);
      const response = await generateUcCertificate(buildUcReportPayload());
      // Keep full payload (we need project_name + result[] for draft header).
      const apiResult = response?.data ?? response;

      if (!response?.status) {
        toast.error(response?.message || "Unable to fetch UC report data", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      const normalizedReport = normalizeUtilizationUcReportResponse(apiResult, {
        selectedFy,
        selectedMonthLabel,
        departmentName: authData?.user?.department_details?.name || "",
        ucType,
      });

      if (!normalizedReport?.certificates?.length) {
        toast.error("Unable to fetch UC report data", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      setUcReportData(normalizedReport);
      setChecksExercisedByScheme(
        buildChecksExercisedState(normalizedReport.certificates)
      );
    } catch (error) {
      toast.error("Something went wrong", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setUcLoading(false);
    }
  };

  const sendUcCertificateOtp = async () => {
    const { data } = await sendUtilizationCertificateOtp();
    return data;
  };

  const handleSaveAndGenerateCertificate = async () => {
    if (!ucReportData?.certificates?.length) return;

    try {
      setUcSaving(true);
      const response = await sendUcCertificateOtp();

      if (!response?.status) {
        toast.error(response?.message || "Unable to send OTP", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      setUcOtp("");
      setUcModalOpen(false);
      setUcOtpModalOpen(true);
      toast.success(response?.message || "OTP sent successfully", {
        position: toast.POSITION.TOP_CENTER,
      });
    } catch (error) {
      toast.error("Something went wrong", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setUcSaving(false);
    }
  };

  const handleCloseUcOtpModal = () => {
    setUcOtpModalOpen(false);
    setUcOtp("");
    setUcOtpLoading(false);
    if (ucReportData?.certificates?.length) {
      setUcModalOpen(true);
    }
  };

  const handleVerifyOtpAndGenerateCertificate = async () => {
    if (!String(ucOtp || "").trim()) {
      toast.error("Please enter OTP", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    try {
      setUcOtpLoading(true);
      const response = await finalizeUcCertificate(
        buildUcVerifyPayload({
          fyId,
          month: selectedMonth,
          ucType,
          checksExercised: checksExercisedByScheme,
          otp: ucOtp,
        })
      );

      if (!response?.status) {
        toast.error(response?.message || "Unable to generate certificate", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      const certificateUrl =
        response?.certificate_url || response?.data?.certificate_url;

      if (!certificateUrl) {
        toast.error("Certificate URL not found", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      window.open(certificateUrl, "_blank", "noopener,noreferrer");
      toast.success(response?.message || "Certificate generated successfully", {
        position: toast.POSITION.TOP_CENTER,
      });
      resetUcFlowState();
    } catch (error) {
      toast.error("Something went wrong", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setUcOtpLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!validateFilters()) return;

    try {
      setLoading(true);
      let apiResult = rawReportData;

      if (!apiResult) {
        const { data } = await fetchUtilizationReport(buildApiPayload());
        if (!data?.status) {
          toast.error(data?.message || "Something went wrong", {
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }
        apiResult = data.result ?? data.data ?? data;
      }

      const exportReport = normalizeUtilizationReportResponse(
        apiResult,
        getFilterContext()
      );

      if (!exportReport.cards?.length) {
        toast.error("No data to export", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      const opened = openUtilizationReportPrintWindow(exportReport, {
        departmentName: authData?.user?.department_details?.name || "",
        userName:
          authData?.user?.name ||
          authData?.user?.username ||
          authData?.user?.email ||
          "",
      });

      if (!opened) {
        toast.error("Please allow pop-ups to export PDF", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      toast.error("Something went wrong", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  };

  const hasFiltersSelected = Boolean(viewType || selectedMonth || rawReportData);

  const getToggleClass = (type) => {
    const isActive = viewType === type;
    return `utilization-report__toggle-btn ${
      isActive
        ? "utilization-report__toggle-btn--active"
        : "utilization-report__toggle-btn--inactive"
    }`;
  };

  return (
    <div className={`utilization-report${isSoeCollapsed ? " utilization-report--collapsed" : ""}`}>
      <div className="utilization-report__header">
        <span className="utilization-report__header-title">
          SOE - STATEMENT OF EXPENDITURE
        </span>
        <button
          type="button"
          className="utilization-report__fold-btn"
          onClick={() => setIsSoeCollapsed((prev) => !prev)}
          aria-label={isSoeCollapsed ? "Expand SOE card" : "Collapse SOE card"}
          title={isSoeCollapsed ? "Expand" : "Collapse"}
        >
          {isSoeCollapsed ? "+" : "−"}
        </button>
      </div>

      {!isSoeCollapsed && (
        <>
          <div className="utilization-report__body">
            <div className="utilization-report__section">
              <span className="utilization-report__label">View Card By</span>
              <div className="utilization-report__toggle-group">
                <button
                  type="button"
                  className={getToggleClass(VIEW_TYPES.DEPARTMENT)}
                  onClick={() => setViewType(VIEW_TYPES.DEPARTMENT)}
                >
                  Department
                </button>
                <button
                  type="button"
                  className={getToggleClass(VIEW_TYPES.SUBHEAD)}
                  onClick={() => setViewType(VIEW_TYPES.SUBHEAD)}
                >
                  Sub-Head
                </button>
              </div>
            </div>

            <div className="utilization-report__section">
              <span className="utilization-report__label">for Month</span>
              <div className="utilization-report__controls">
                <FormControl size="small" className="utilization-report__month-form">
                  <Select
                    displayEmpty
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select month</em>
                    </MenuItem>
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <button
                  type="button"
                  className="utilization-report__action-btn utilization-report__action-btn--load"
                  onClick={handleLoadCard}
                  disabled={loading}
                >
                  Load Card
                </button>

                <button
                  type="button"
                  className="utilization-report__action-btn utilization-report__action-btn--reset"
                  onClick={handleReset}
                  disabled={loading || !hasFiltersSelected}
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="utilization-report__actions">
              <Tooltip title="export to pdf" arrow>
                <button
                  type="button"
                  className="utilization-report__export"
                  onClick={handleExportPDF}
                  disabled={loading}
                  aria-label="export to pdf"
                >
                  <i className="icon-picture_as_pdf text-danger-100" />
                </button>
              </Tooltip>

              {isL3User && (
                <button
                  type="button"
                  className="utilization-report__uc-btn"
                  onClick={handleOpenUcModal}
                  disabled={loading || !hasReportCards}
                >
                  Generate UC Report
                </button>
              )}
            </div>
          </div>

          {reportData && <UtilizationReportCards reportData={reportData} />}
        </>
      )}

      <UtilizationUcReportModal
        open={ucModalOpen}
        onClose={handleCloseUcModal}
        selectedFyLabel={selectedFy?.year || "-"}
        selectedMonthLabel={selectedMonthLabel}
        ucType={ucType}
        onUcTypeChange={setUcType}
        onGenerate={handleGenerateUcReport}
        onSaveAndGenerate={handleSaveAndGenerateCertificate}
        loading={ucLoading}
        saving={ucSaving}
        ucReportData={ucReportData}
        checksExercisedByScheme={checksExercisedByScheme}
        onCheckChange={handleCheckChange}
        onCheckAdd={handleCheckAdd}
        onCheckRemove={handleCheckRemove}
      />

      <UtilizationUcOtpModal
        open={ucOtpModalOpen}
        onClose={handleCloseUcOtpModal}
        otp={ucOtp}
        onOtpChange={setUcOtp}
        onSubmit={handleVerifyOtpAndGenerateCertificate}
        loading={ucOtpLoading}
      />
    </div>
  );
};

export default UtilizationReport;
